/**
 * Sprint P32 · Intelligent Setup Propagation audit trace.
 * Renders on the project Health Dashboard so users can prove that the
 * setup values are consumed by every downstream module.
 */
import { useQuery } from "@tanstack/react-query";
import { CheckCircle, Warning, Circle, XCircle } from "@phosphor-icons/react";
import { getSetupPropagation } from "@/lib/api";
import { WizardCard, KV, Pill } from "@/pages/wizard/_shared";


const STATUS_ICON = {
  consumed: <CheckCircle size={12} weight="fill" className="text-emerald-600" />,
  no_data:  <Circle      size={12} className="text-slate-400" />,
  not_run:  <Circle      size={12} className="text-slate-400" />,
  error:    <XCircle     size={12} weight="fill" className="text-red-600" />,
};
const STATUS_LABEL = {
  consumed: "Consumed",  no_data: "No data",  not_run: "Not run", error: "Error",
};


export default function SetupPropagationPanel({ projectId }) {
  const q = useQuery({
    queryKey: ["setup-propagation", projectId],
    queryFn:  () => getSetupPropagation(projectId),
    enabled:  !!projectId,
    refetchInterval: 60_000,
  });

  const d = q.data;
  if (q.isLoading) return null;
  if (q.isError || !d)
    return <div className="text-[11px] text-slate-500">Propagation trace unavailable.</div>;

  const s = d.summary || {};
  const canonical = d.canonical_setup || {};

  return (
    <WizardCard
        title="Intelligent Setup Propagation"
        subtitle="Every downstream module is checked against the canonical setup values"
        testId="w-setup-propagation">
      <div className="flex items-center gap-2 mb-3">
        <Pill tone={s.propagation_ok ? "green" : "amber"}
              testId="w-setup-propagation-pill">
          {s.propagation_ok ? "All consumed correctly" : "Mismatches detected"}
        </Pill>
        <div className="text-[11px] text-slate-600">
          <b>{s.consumed}</b> / {s.total_modules} modules consuming setup values ·{" "}
          <b>{s.mismatches}</b> mismatch{s.mismatches === 1 ? "" : "es"}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4 text-[11px]"
              data-testid="w-setup-propagation-canonical">
        <KV k="Country"      v={canonical.country || "—"} />
        <KV k="Commodity"    v={canonical.commodity || "—"} />
        <KV k="Standard"     v={canonical.reporting_standard || "—"} />
        <KV k="Cutoff"       v={canonical.cutoff ?? "—"} />
        <KV k="CRS · EPSG"   v={`${canonical.crs || "—"} · ${canonical.epsg || "—"}`} />
        <KV k="Unit system"  v={canonical.unit_system || "—"} />
        <KV k="Grade unit"   v={canonical.grade_unit || "—"} />
        <KV k="Density unit" v={canonical.density_unit || "—"} />
      </div>

      <div className="rounded border border-slate-200 overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="text-left px-2 py-1">Module</th>
              <th className="text-left px-2 py-1">Consumes</th>
              <th className="text-center px-2 py-1">Status</th>
              <th className="text-left px-2 py-1">Evidence / Mismatches</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(d.modules || {}).map(([name, m]) => {
              const mismatch = (m.mismatches || []).length > 0;
              return (
                <tr key={name}
                    data-testid={`w-setup-propagation-row-${name}`}
                    className={"border-t border-slate-200 " + (mismatch ? "bg-amber-50" : "")}>
                  <td className="px-2 py-1 font-bold text-slate-800">
                    {name.replace(/_/g, " ")}
                  </td>
                  <td className="px-2 py-1 text-slate-600">
                    {(m.consumes || []).join(", ")}
                  </td>
                  <td className="text-center px-2 py-1">
                    <span className="inline-flex items-center gap-1 text-[10px]">
                      {STATUS_ICON[m.status] || <Circle size={12} />}
                      {STATUS_LABEL[m.status] || m.status}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-[10px] text-slate-500">
                    {mismatch ? (
                      (m.mismatches || []).map((mm, i) => (
                        <div key={i} className="text-amber-700 flex items-center gap-1">
                          <Warning size={9} weight="fill" />
                          {mm.field}: canonical <b>{String(mm.canonical)}</b> ≠ module <b>{String(mm.module)}</b>
                        </div>
                      ))
                    ) : m.evidence ? (
                      <span className="text-slate-500 truncate block max-w-[300px]">
                        {Object.entries(m.evidence).slice(0, 3).map(([k, v]) =>
                          `${k}=${v == null ? "—" : JSON.stringify(v).replace(/["\\]/g, "")}`
                        ).join("  ")}
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </WizardCard>
  );
}
